import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Receipt, Camera, ArrowLeft, Loader2, Store, Star, 
  ShoppingBag, Calendar, Clock, Users, Trash2,
  AlertTriangle, Check, X, Heart, Search, Filter, Globe, ImagePlus, Sparkles,
  Edit2, Save, Home, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const CATEGORY_EMOJIS = {
  fruits_legumes: "🥬",
  viande: "🥩",
  poisson: "🐟",
  produits_laitiers: "🥛",
  epicerie: "🛒",
  boissons: "🥤",
  surgeles: "🧊",
  hygiene: "🧴",
  autre: "📦"
};

const MyReceiptsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("scan");
  const [receipts, setReceipts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [chefGustoRecipes, setChefGustoRecipes] = useState([]);
  const [loadingChefGusto, setLoadingChefGusto] = useState(false);
  const [chefGustoAsked, setChefGustoAsked] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // {index, name, price}
  const [isEditMode, setIsEditMode] = useState(false);
  const [showHouseholdDialog, setShowHouseholdDialog] = useState(false);
  const [householdStep, setHouseholdStep] = useState("choice"); // "choice" | "category"
  const [selectedCategory, setSelectedCategory] = useState("groceries");
  const [userHousehold, setUserHousehold] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    // Check if user has a household
    const checkHousehold = async () => {
      try {
        const res = await axios.get(`${API}/household`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserHousehold(res.data);
      } catch {}
    };
    checkHousehold();
  }, [token]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchReceipts();
    } else if (activeTab === "stores") {
      fetchStores();
    }
  }, [activeTab, token]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/receipts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Ask Chef Gusto to generate recipes based on receipt products
  const askChefGusto = async () => {
    if (!scanResult?.receipt?.products || scanResult.receipt.products.length === 0) {
      toast.error("Aucun produit détecté pour générer des recettes");
      return;
    }
    
    setLoadingChefGusto(true);
    setChefGustoAsked(true);
    try {
      // Extract food product names for search
      const products = scanResult.receipt.products;
      const ingredientNames = products
        .filter(p => p.category && ['fruits_legumes', 'viande', 'poisson', 'produits_laitiers', 'epicerie'].includes(p.category))
        .map(p => p.name)
        .slice(0, 5);
      
      if (ingredientNames.length === 0) {
        ingredientNames.push(...products.slice(0, 3).map(p => p.name));
      }
      
      const response = await axios.post(`${API}/recipes/search-web`, {
        ingredients: ingredientNames,
        limit: 3
      });
      
      if (response.data.recipes && response.data.recipes.length > 0) {
        setChefGustoRecipes(response.data.recipes);
        toast.success(`Chef Gusto vous propose ${response.data.recipes.length} recettes !`);
      } else {
        toast.info("Chef Gusto n'a pas trouvé de recettes pour ces produits");
      }
    } catch (err) {
      console.error("Chef Gusto error:", err);
      toast.error("Erreur lors de la génération des recettes");
    } finally {
      setLoadingChefGusto(false);
    }
  };
  
  // Navigate to Chef Gusto recipe detail page
  const viewChefGustoRecipe = (recipe) => {
    sessionStorage.setItem('webRecipe', JSON.stringify(recipe));
    navigate(`/web-recipe/${recipe.id}`);
  };

  const triggerCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  const triggerGallery = () => {
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
      galleryInputRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setScanResult(null);
    };
    reader.readAsDataURL(file);
  };

  const scanReceipt = async () => {
    if (!imagePreview) return;

    setScanning(true);
    setScanSuccess(false);
    setChefGustoRecipes([]);
    setChefGustoAsked(false);
    try {
      const res = await axios.post(
        `${API}/receipts/scan`,
        { image_base64: imagePreview },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show success animation first
      setScanSuccess(true);
      setScanning(false);
      
      // After 1.5 seconds, show the results
      setTimeout(() => {
        setScanResult(res.data);
        setScanSuccess(false);
        
        if (res.data.recalls_found?.length > 0) {
          toast.warning(`⚠️ ${res.data.recalls_found.length} produit(s) rappelé(s) détecté(s) !`);
        } else {
          toast.success("Ticket analysé avec succès !");
        }
        
        // If user has a household, show dialog to choose
        if (userHousehold && res.data.receipt?.id) {
          setTimeout(() => setShowHouseholdDialog(true), 500);
        }
      }, 1500);
      
    } catch (error) {
      toast.error("Erreur lors de l'analyse");
      console.error(error);
      setScanning(false);
    }
  };

  const toggleFavoriteStore = async (storeId) => {
    try {
      const res = await axios.post(
        `${API}/stores/${storeId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStores(prev => prev.map(s => 
        s.id === storeId ? { ...s, is_favorite: res.data.is_favorite } : s
      ));
      toast.success(res.data.is_favorite ? "Boutique ajoutée aux favoris" : "Boutique retirée des favoris");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const deleteReceipt = async () => {
    if (!deleteDialog) return;
    try {
      await axios.delete(`${API}/receipts/${deleteDialog}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(prev => prev.filter(r => r.id !== deleteDialog));
      setDeleteDialog(null);
      toast.success("Ticket supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetScan = () => {
    setImagePreview(null);
    setScanResult(null);
    setScanSuccess(false);
    setChefGustoRecipes([]);
    setChefGustoAsked(false);
    setIsEditMode(false);
    setEditingProduct(null);
  };

  // Edit product in scan result (before saving)
  const updateProductInScan = (index, field, value) => {
    if (!scanResult?.receipt?.products) return;
    
    const updatedProducts = [...scanResult.receipt.products];
    updatedProducts[index] = { ...updatedProducts[index], [field]: value };
    
    // Recalculate total
    const newTotal = updatedProducts.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
    
    setScanResult({
      ...scanResult,
      receipt: {
        ...scanResult.receipt,
        products: updatedProducts,
        total: newTotal
      }
    });
  };

  // Delete product from scan result
  const deleteProductFromScan = (index) => {
    if (!scanResult?.receipt?.products) return;
    
    const updatedProducts = scanResult.receipt.products.filter((_, i) => i !== index);
    const newTotal = updatedProducts.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
    
    setScanResult({
      ...scanResult,
      receipt: {
        ...scanResult.receipt,
        products: updatedProducts,
        total: newTotal
      }
    });
    
    toast.success("Produit supprimé");
  };

  // Save edited product
  const saveEditedProduct = () => {
    if (editingProduct) {
      updateProductInScan(editingProduct.index, "name", editingProduct.name);
      updateProductInScan(editingProduct.index, "price", parseFloat(editingProduct.price) || 0);
      setEditingProduct(null);
      toast.success("Produit modifié");
    }
  };

  // Assign receipt to household or keep personal
  const assignReceiptToHousehold = async (addToHousehold, category) => {
    if (!scanResult?.receipt?.id) return;
    
    try {
      await axios.put(
        `${API}/receipts/${scanResult.receipt.id}/household`,
        { 
          add_to_household: addToHousehold,
          expense_category: category || selectedCategory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (addToHousehold) {
        toast.success("Ticket ajouté au budget du foyer !");
      } else {
        toast.success("Ticket enregistré en personnel");
      }
      setShowHouseholdDialog(false);
      setHouseholdStep("choice");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-red-500/10 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/discover"
            className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal">
                Mes Tickets
              </h1>
              <p className="text-muted-foreground text-sm">
                Scannez, conservez, cuisinez !
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-4xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-3 mb-6 h-12 rounded-2xl bg-muted/50">
            <TabsTrigger 
              value="scan" 
              className="rounded-xl data-[state=active]:bg-amber-500 data-[state=active]:text-white gap-1"
            >
              <Camera className="w-4 h-4" />
              Scanner
            </TabsTrigger>
            <TabsTrigger 
              value="history"
              className="rounded-xl data-[state=active]:bg-amber-500 data-[state=active]:text-white gap-1"
            >
              <Receipt className="w-4 h-4" />
              Historique
            </TabsTrigger>
            <TabsTrigger 
              value="stores"
              className="rounded-xl data-[state=active]:bg-amber-500 data-[state=active]:text-white gap-1"
            >
              <Store className="w-4 h-4" />
              Boutiques
            </TabsTrigger>
          </TabsList>

          {/* Scanner Tab */}
          <TabsContent value="scan" className="mt-0 space-y-6">
            {!scanResult ? (
              <>
                {/* Image Capture */}
                <Card className="shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    {imagePreview ? (
                      <div className="relative">
                        <img 
                          src={imagePreview} 
                          alt="Ticket" 
                          className="w-full aspect-[3/4] object-cover"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                          onClick={resetScan}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                        {scanning && (
                          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                            <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
                            <p className="text-white font-medium">Analyse du ticket...</p>
                            <p className="text-white/60 text-sm mt-1">Extraction des produits</p>
                          </div>
                        )}
                        {scanSuccess && (
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="absolute inset-0 bg-green-500/95 flex flex-col items-center justify-center"
                          >
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                              className="w-24 h-24 rounded-full bg-white flex items-center justify-center mb-4 shadow-lg"
                            >
                              <Check className="w-14 h-14 text-green-500" strokeWidth={3} />
                            </motion.div>
                            <motion.p 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3 }}
                              className="text-white font-bold text-xl"
                            >
                              Ticket analysé !
                            </motion.p>
                          </motion.div>
                        )}
                      </div>
                    ) : (
                      <div 
                        className="aspect-[3/4] flex flex-col items-center justify-center bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={triggerCamera}
                      >
                        <div className="w-20 h-20 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                          <Receipt className="w-10 h-10 text-amber-600" />
                        </div>
                        <p className="font-medium text-charcoal">Photographier un ticket</p>
                        <p className="text-sm text-muted-foreground mt-1">ou sélectionner depuis la galerie</p>
                      </div>
                    )}

                    {/* Camera Input */}
                    <input
                      id="receipt-camera-input"
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageChange}
                      className="sr-only"
                    />
                    
                    {/* Gallery Input */}
                    <input
                      id="receipt-gallery-input"
                      ref={galleryInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="sr-only"
                    />
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {!imagePreview ? (
                    <div className="flex flex-col gap-3">
                      <Button
                        onClick={triggerCamera}
                        className="w-full h-14 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-lg gap-2"
                      >
                        <Camera className="w-5 h-5" />
                        Prendre une photo
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={triggerGallery}
                        className="w-full h-12 rounded-2xl gap-2"
                      >
                        <ImagePlus className="w-5 h-5" />
                        Choisir depuis la galerie
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={scanReceipt}
                      disabled={scanning}
                      className="w-full h-14 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-lg gap-2"
                    >
                      {scanning ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Analyse en cours...
                        </>
                      ) : (
                        <>
                          <Receipt className="w-5 h-5" />
                          Analyser le ticket
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Tips */}
                <Card className="bg-amber-500/5 border-amber-500/20">
                  <CardContent className="p-4">
                    <h3 className="font-medium text-charcoal flex items-center gap-2 mb-2">
                      <Receipt className="w-4 h-4 text-amber-600" />
                      Pourquoi scanner vos tickets ?
                    </h3>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Gardez une trace de vos achats alimentaires</li>
                      <li>• Recevez des suggestions de recettes adaptées</li>
                      <li>• Soyez alerté en cas de rappel produit</li>
                      <li>• Retrouvez vos boutiques favorites</li>
                    </ul>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* Scan Results */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Store Info */}
                <Card className="shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Store className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        {scanResult.receipt.store_id ? (
                          <Link 
                            to={`/stores/${scanResult.receipt.store_id}`}
                            className="font-heading font-semibold text-lg text-primary hover:underline flex items-center gap-2"
                          >
                            {scanResult.receipt.store_name}
                            <Globe className="w-4 h-4" />
                          </Link>
                        ) : (
                          <h2 className="font-heading font-semibold text-lg">{scanResult.receipt.store_name}</h2>
                        )}
                        {scanResult.receipt.store_address && (
                          <p className="text-sm text-muted-foreground">{scanResult.receipt.store_address}</p>
                        )}
                      </div>
                      {scanResult.receipt.total && (
                        <Badge className="bg-green-500 text-lg px-3 py-1">
                          {typeof scanResult.receipt.total === 'number' 
                            ? scanResult.receipt.total.toFixed(2) 
                            : scanResult.receipt.total}€
                        </Badge>
                      )}                    </div>
                    
                    {/* Additional Store Info */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {scanResult.receipt.store_phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>📞</span>
                          <span>{scanResult.receipt.store_phone}</span>
                        </div>
                      )}
                      {scanResult.receipt.store_siret && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>🏢</span>
                          <span>SIRET: {scanResult.receipt.store_siret}</span>
                        </div>
                      )}
                      {(scanResult.receipt.date || scanResult.receipt.time) && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {scanResult.receipt.date && new Date(scanResult.receipt.date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                            {scanResult.receipt.time && ` à ${scanResult.receipt.time}`}
                          </span>
                        </div>
                      )}
                      {scanResult.receipt.receipt_number && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>🧾</span>
                          <span>N° {scanResult.receipt.receipt_number}</span>
                        </div>
                      )}
                      {scanResult.receipt.payment_method && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>💳</span>
                          <span>{scanResult.receipt.payment_method}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Product Recalls Alert */}
                {scanResult.recalls_found?.length > 0 && (
                  <Card className="border-red-500 bg-red-50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold text-red-700">⚠️ Produits rappelés détectés !</h3>
                          {scanResult.recalls_found.map((recall, index) => (
                            <div key={index} className="mt-2 p-2 bg-white rounded-lg">
                              <p className="font-medium text-red-600">{recall.product}</p>
                              <p className="text-sm text-red-500">{recall.recall.reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Products List */}
                <Card className="shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-semibold flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-amber-600" />
                        Produits ({scanResult.receipt.products?.length || 0})
                      </h3>
                      <Button
                        variant={isEditMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => setIsEditMode(!isEditMode)}
                        className="gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        {isEditMode ? "Terminer" : "Ajuster"}
                      </Button>
                    </div>
                    
                    {isEditMode && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg mb-3">
                        Cliquez sur un produit pour modifier son nom ou son prix, ou supprimez les articles non-alimentaires.
                      </p>
                    )}
                    
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {scanResult.receipt.products?.map((product, index) => (
                        <motion.div 
                          key={index} 
                          layout
                          className={`flex items-center justify-between py-2 px-2 rounded-lg border-b last:border-0 ${
                            isEditMode ? 'hover:bg-amber-50 cursor-pointer' : ''
                          } ${editingProduct?.index === index ? 'bg-amber-50 border border-amber-200' : ''}`}
                          onClick={() => {
                            if (isEditMode && editingProduct?.index !== index) {
                              setEditingProduct({
                                index,
                                name: product.name,
                                price: product.price?.toString() || "0"
                              });
                            }
                          }}
                        >
                          {editingProduct?.index === index ? (
                            // Edit mode for this product
                            <div className="flex-1 space-y-2">
                              <div>
                                <Label className="text-xs">Nom du produit</Label>
                                <Input
                                  value={editingProduct.name}
                                  onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                                  className="h-8 text-sm"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                  <Label className="text-xs">Prix (€)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editingProduct.price}
                                    onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                                    className="h-8 text-sm"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                <Button 
                                  size="sm" 
                                  onClick={(e) => { e.stopPropagation(); saveEditedProduct(); }}
                                  className="h-8 gap-1"
                                >
                                  <Save className="w-3 h-3" />
                                  OK
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); setEditingProduct(null); }}
                                  className="h-8"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            // Normal display
                            <>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="flex-shrink-0">{CATEGORY_EMOJIS[product.category] || "📦"}</span>
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium block truncate">{product.name}</span>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    {product.quantity && <span>{product.quantity}</span>}
                                    {product.unit_price && <span>× {typeof product.unit_price === 'number' ? product.unit_price.toFixed(2) : product.unit_price}€</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {product.price && (
                                  <span className="text-sm font-semibold text-charcoal">
                                    {typeof product.price === 'number' ? product.price.toFixed(2) : product.price}€
                                  </span>
                                )}
                                {isEditMode && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteProductFromScan(index);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </>
                          )}
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Totals */}
                    <div className="mt-4 pt-3 border-t space-y-1">
                      {scanResult.receipt.subtotal && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Sous-total</span>
                          <span>{typeof scanResult.receipt.subtotal === 'number' ? scanResult.receipt.subtotal.toFixed(2) : scanResult.receipt.subtotal}€</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold">
                        <span>Total</span>
                        <span className="text-lg text-green-600">
                          {scanResult.receipt.total 
                            ? (typeof scanResult.receipt.total === 'number' ? scanResult.receipt.total.toFixed(2) : scanResult.receipt.total)
                            : '—'}€
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Matching Recipes */}
                {scanResult.matching_recipes?.length > 0 && (
                  <Card className="shadow-lg">
                    <CardContent className="p-4">
                      <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                        <GustoIcon className="w-5 h-5" />
                        Recettes possibles
                      </h3>
                      <div className="space-y-2">
                        {scanResult.matching_recipes.slice(0, 5).map((recipe) => (
                          <Link key={recipe.id} to={`/recipe/${recipe.id}`}>
                            <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted/50 transition-colors">
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center overflow-hidden">
                                {recipe.final_image ? (
                                  <img 
                                    src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                                    alt={recipe.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <GustoIcon className="w-5 h-5" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{recipe.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {recipe.matched_ingredients} ingrédients correspondants
                                </p>
                              </div>
                              <Badge className="bg-green-500/20 text-green-700">
                                {recipe.match_percent}%
                              </Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* No local recipes message */}
                {(!scanResult.matching_recipes || scanResult.matching_recipes.length === 0) && !chefGustoAsked && (
                  <Card className="p-4 text-center bg-muted/30">
                    <GustoLogo size="md" className="mx-auto opacity-40 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Aucune recette de la communauté ne correspond
                    </p>
                  </Card>
                )}

                {/* Ask Chef Gusto Button */}
                {!chefGustoAsked && (
                  <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-charcoal text-sm">Chef Gusto</h3>
                          <p className="text-xs text-muted-foreground">
                            3 recettes personnalisées avec vos achats
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={askChefGusto}
                        disabled={loadingChefGusto}
                        size="sm"
                        className="w-full mt-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                      >
                        {loadingChefGusto ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Génération...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Demander au Chef Gusto
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Chef Gusto Loading */}
                {loadingChefGusto && (
                  <Card className="p-6 text-center">
                    <Loader2 className="w-10 h-10 mx-auto text-cyan-500 animate-spin mb-2" />
                    <p className="font-medium text-charcoal text-sm">Chef Gusto prépare vos recettes...</p>
                  </Card>
                )}

                {/* Chef Gusto Recipes */}
                {chefGustoAsked && !loadingChefGusto && chefGustoRecipes.length > 0 && (
                  <Card className="shadow-lg border-cyan-200">
                    <CardContent className="p-4">
                      <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-cyan-600" />
                        Recettes du Chef Gusto
                        <Badge className="bg-cyan-500">{chefGustoRecipes.length}</Badge>
                      </h3>
                      <div className="space-y-2">
                        {chefGustoRecipes.map((recipe) => (
                          <div 
                            key={recipe.id}
                            onClick={() => viewChefGustoRecipe(recipe)}
                            className="flex items-center gap-3 p-2 rounded-xl hover:bg-cyan-50 transition-colors cursor-pointer"
                          >
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                              <GustoIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{recipe.name}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {recipe.prep_time && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {recipe.prep_time}
                                  </span>
                                )}
                                {recipe.ingredients?.length > 0 && (
                                  <span>{recipe.ingredients.length} ing.</span>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                              <Sparkles className="w-3 h-3 mr-1" />
                              IA
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={resetScan}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Nouveau scan
                  </Button>
                  <Button
                    onClick={() => {
                      setActiveTab("history");
                      resetScan();
                    }}
                    className="flex-1 h-12 rounded-xl bg-amber-500 hover:bg-amber-600"
                  >
                    Voir l'historique
                  </Button>
                </div>
              </motion.div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : receipts.length === 0 ? (
              <Card className="p-8 text-center">
                <Receipt className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">Aucun ticket enregistré</p>
                <Button 
                  onClick={() => setActiveTab("scan")}
                  className="mt-4 rounded-full bg-amber-500"
                >
                  Scanner un ticket
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {receipts.map((receipt) => (
                  <motion.div
                    key={receipt.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedReceipt(receipt)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                              <Store className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <h3 className="font-medium">{receipt.store_name}</h3>
                              <p className="text-xs text-muted-foreground">
                                {new Date(receipt.created_at).toLocaleDateString('fr-FR')} • {receipt.products?.length || 0} produits
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {receipt.total && (
                              <Badge variant="outline">{receipt.total.toFixed(2)}€</Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteDialog(receipt.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Stores Tab */}
          <TabsContent value="stores" className="mt-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : stores.length === 0 ? (
              <Card className="p-8 text-center">
                <Store className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">Aucune boutique</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Scannez des tickets pour voir vos boutiques
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {stores.map((store) => (
                  <Card key={store.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                            <Store className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <h3 className="font-medium">{store.name}</h3>
                            <p className="text-xs text-muted-foreground">
                              {store.receipts_count} ticket{store.receipts_count > 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleFavoriteStore(store.id)}
                          className={store.is_favorite ? "text-amber-500" : "text-muted-foreground"}
                        >
                          <Star className={`w-5 h-5 ${store.is_favorite ? "fill-amber-500" : ""}`} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Receipt Detail Dialog */}
      <Dialog open={!!selectedReceipt} onOpenChange={() => setSelectedReceipt(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          {selectedReceipt && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5 text-amber-600" />
                  {selectedReceipt.store_name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedReceipt.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                  {selectedReceipt.total && (
                    <Badge className="bg-green-500">{selectedReceipt.total.toFixed(2)}€</Badge>
                  )}
                </div>

                <div className="border rounded-xl p-3 space-y-2">
                  <h4 className="font-medium text-sm">Produits ({selectedReceipt.products?.length || 0})</h4>
                  {selectedReceipt.products?.map((product, index) => (
                    <div key={index} className="flex items-center justify-between py-1 border-b last:border-0 text-sm">
                      <div className="flex items-center gap-2">
                        <span>{CATEGORY_EMOJIS[product.category] || "📦"}</span>
                        <span>{product.name}</span>
                      </div>
                      {product.price && (
                        <span className="text-muted-foreground">{product.price.toFixed(2)}€</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedReceipt(null)} className="rounded-full">
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer ce ticket ?</DialogTitle>
            <DialogDescription>
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="rounded-full">
              Annuler
            </Button>
            <Button variant="destructive" onClick={deleteReceipt} className="rounded-full">
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Household Choice Dialog */}
      <Dialog open={showHouseholdDialog} onOpenChange={(open) => { setShowHouseholdDialog(open); if (!open) setHouseholdStep("choice"); }}>
        <DialogContent className="sm:max-w-md" data-testid="household-dialog">
          {householdStep === "choice" ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-emerald-600" />
                  Ajouter au foyer ?
                </DialogTitle>
                <DialogDescription>
                  Ce ticket de <strong>{scanResult?.receipt?.total?.toFixed(2)}€</strong> chez {scanResult?.receipt?.store_name} doit-il être comptabilisé dans le budget du foyer ?
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-4">
                <Button
                  variant="outline"
                  className="h-24 flex-col gap-2"
                  onClick={() => assignReceiptToHousehold(false, "groceries")}
                  data-testid="household-personal-btn"
                >
                  <User className="w-8 h-8 text-blue-500" />
                  <span>Personnel</span>
                </Button>
                <Button
                  className="h-24 flex-col gap-2 bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => setHouseholdStep("category")}
                  data-testid="household-foyer-btn"
                >
                  <Home className="w-8 h-8" />
                  <span>Foyer ({userHousehold?.name})</span>
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-emerald-600" />
                  Catégorie de dépense
                </DialogTitle>
                <DialogDescription>
                  Dans quel sous-budget du foyer comptabiliser ce ticket ?
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-4">
                {[
                  { key: "groceries", emoji: "🛒", name: "Courses", color: "bg-green-500" },
                  { key: "restaurant", emoji: "🍽️", name: "Sorties/Resto", color: "bg-amber-500" },
                  { key: "pro_meals", emoji: "💼", name: "Repas pro", color: "bg-blue-500" },
                  { key: "friends_meals", emoji: "👥", name: "Repas amis", color: "bg-violet-500" }
                ].map((cat) => (
                  <Button
                    key={cat.key}
                    variant={selectedCategory === cat.key ? "default" : "outline"}
                    className={`h-20 flex-col gap-1 ${selectedCategory === cat.key ? cat.color + " hover:opacity-90" : ""}`}
                    onClick={() => setSelectedCategory(cat.key)}
                    data-testid={`category-${cat.key}`}
                  >
                    <span className="text-2xl">{cat.emoji}</span>
                    <span className="text-xs">{cat.name}</span>
                  </Button>
                ))}
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setHouseholdStep("choice")} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                </Button>
                <Button 
                  onClick={() => assignReceiptToHousehold(true, selectedCategory)} 
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  data-testid="confirm-category-btn"
                >
                  <Check className="w-4 h-4 mr-1" /> Confirmer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyReceiptsPage;
